import {
  AfterViewInit,
  Component,
  ElementRef,
  computed,
  effect,
  input,
  signal,
  viewChild,
  inject,
} from '@angular/core';

import { GsapService } from '@app/shared/animations/gsap.service';
import { SharedModule } from '@app/shared/shared.module';
import { clamp01 } from '@app/progress-line/progress-line.math';

@Component({
  standalone: true,
  selector: 'slotcom-progress-indicator',
  imports: [SharedModule],
  templateUrl: './progress-indicator.component.html',
  styleUrls: ['./progress-indicator.component.scss'],
})
export class ProgressIndicatorComponent implements AfterViewInit {
  private readonly gsap = inject(GsapService);

  readonly current = input.required<number>();
  readonly next = input.required<number>();
  readonly total = input.required<number>();
  readonly segmentProgress = input.required<number>();
  readonly trackLeftPx = input(0);
  readonly trackWidthPx = input.required<number>();
  readonly scrollContainerEl = input<HTMLElement | null>(null);

  readonly bubbleRef = viewChild.required<ElementRef<HTMLElement>>('bubble');

  private get bubbleEl(): HTMLElement {
    return this.bubbleRef().nativeElement;
  }

  private readonly scrollLeft = signal(0);
  private readonly containerWidth = signal(0);
  private readonly bubbleW = signal(0);

  private readonly progress = computed(() => clamp01(this.segmentProgress()));

  private readonly naturalContentLeft = computed(
    () => this.trackLeftPx() + this.progress() * this.trackWidthPx() - this.bubbleW() / 2
  );

  private readonly viewportX = computed(() => this.naturalContentLeft() - this.scrollLeft());

  private readonly ready = computed(() => this.containerWidth() > 0 && this.bubbleW() > 0);

  private readonly target = computed(() => {
    if (!this.ready()) return { x: 0, pinned: 'none' as const };

    const vpW = this.containerWidth();
    const x = this.viewportX();
    const bw = this.bubbleW();
    const right = x + bw;

    if (x >= 0 && right <= vpW) return { x, pinned: 'none' as const };
    if (x < 0) return { x: 0, pinned: 'left' as const };
    return { x: vpW - bw, pinned: 'right' as const };
  });

  private prevProgress = 0;
  private initialized = false;

  constructor() {
    effect((onCleanup) => {
      const scroller = this.scrollContainerEl();
      const bubble = this.bubbleEl;
      if (!scroller || !bubble) return;

      this.readMetrics(scroller, bubble);

      const onScroll = () => this.scrollLeft.set(scroller.scrollLeft);
      scroller.addEventListener('scroll', onScroll, { passive: true });

      const ro = new ResizeObserver(() => this.readMetrics(scroller, bubble));
      ro.observe(scroller);
      ro.observe(bubble);

      onCleanup(() => {
        scroller.removeEventListener('scroll', onScroll);
        ro.disconnect();
      });
    });

    effect(() => {
      const { x, pinned } = this.target();
      const p = this.progress();
      const el = this.bubbleEl;

      if (!this.ready()) {
        el.setAttribute('data-pinned', 'none');
        return;
      }

      const largeJump = Math.abs(p - this.prevProgress) >= 0.15;
      const duration = largeJump ? 0.45 : 0.2;
      const ease = largeJump ? 'power2.out' : 'power1.out';

      if (!this.initialized) {
        this.gsap.set(el, { x, opacity: 0 });
        el.setAttribute('data-pinned', pinned);
        this.initialized = true;
        this.gsap.to(el, { opacity: 1 }, { duration: 0.4, ease: 'power1.out' });
      } else {
        el.setAttribute('data-pinned', pinned);
        this.gsap.to(el, { x, opacity: 1 }, { duration, ease });
      }

      this.prevProgress = p;
    });
  }

  ngAfterViewInit(): void {
    this.gsap.set(this.bubbleEl, { opacity: 0 });
  }

  private readMetrics(scroller: HTMLElement, bubble: HTMLElement): void {
    this.containerWidth.set(scroller.clientWidth);
    this.scrollLeft.set(scroller.scrollLeft);
    this.bubbleW.set(bubble.offsetWidth);
  }
}
